"use client";

import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Badge } from "../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Loader2, Users, UserPlus, CheckCircle, AlertCircle, Shield, Trash2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { useAuth } from "@/contexts/AuthContext";
import { useEngagement } from "@/contexts/EngagementContext";
import { apiService } from "@/services/api-service";

interface User {
  id: string;
  user_name: string;
  email: string;
}

interface TeamMember {
  id: string;
  user_name?: string;
  first_name?: string;
  last_name?: string;
  email: string;
  role: string;
  EngagementUser?: {
    role: string;
  };
}

export default function AccessAndRoles() {
  const { user } = useAuth();
  const { currentEngagement } = useEngagement();
  const [firmUsers, setFirmUsers] = useState<User[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [selectedRole, setSelectedRole] = useState<string>("associate");
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<{ id: string; name: string } | null>(null);

  const roleOptions = [
    { value: "eqr_partner", label: "EQR Partner" },
    { value: "eqr_manager", label: "EQR Manager" },
    { value: "associate", label: "Associate" },
    { value: "article", label: "Article" },
  ];

  // Load current team list for all users (whenever an engagement is selected).
  // hasAccess (add/remove) is computed from current user's role in the team.
  useEffect(() => {
    if (!currentEngagement) {
      setTeamMembers([]);
      setCheckingAccess(false);
      setHasAccess(false);
      return;
    }

    let cancelled = false;

    const loadTeamAndAccess = async () => {
      try {
        console.log('[AccessAndRoles] Loading team for engagement:', currentEngagement!.id);
        const team = await apiService.getEngagementTeam(currentEngagement!.id);
        if (cancelled) return;

        console.log('[AccessAndRoles] Team members received:', team);

        const normalizedTeam = (Array.isArray(team) ? team : []).map((member: any) => ({
          id: member.id,
          user_name: member.user_name || `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email,
          email: member.email,
          role: member.role || member.EngagementUser?.role || 'associate'
        }));
        setTeamMembers(normalizedTeam);
        console.log('[AccessAndRoles] Normalized team members:', normalizedTeam);

        if (user) {
          const currentUserMember = normalizedTeam.find((member: any) => String(member.id) === String(user.id));
          console.log('[AccessAndRoles] Current user in team:', currentUserMember);
          const canAddRemove =
            currentUserMember &&
            (currentUserMember.role === 'engagement_partner' || currentUserMember.role === 'engagement_manager');
          setHasAccess(!!canAddRemove);
          console.log('[AccessAndRoles] User has add/remove access:', !!canAddRemove, 
            currentUserMember ? `(role: ${currentUserMember.role})` : '(not in team)');
        } else {
          setHasAccess(false);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Error loading team / checking access:', error);
          setTeamMembers([]);
          setHasAccess(false);
        }
      } finally {
        if (!cancelled) setCheckingAccess(false);
      }
    };

    loadTeamAndAccess();
    return () => { cancelled = true; };
  }, [currentEngagement, user]);

  // Load firm users only for users who can add (engagement partner / manager)
  useEffect(() => {
    const loadFirmUsers = async () => {
      if (!user?.firm_id || !hasAccess) {
        if (!hasAccess) setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const users = await apiService.getFirmUsers();
        setFirmUsers(users);
      } catch (error) {
        console.error('Error loading firm users:', error);
        setError('Failed to load users');
      } finally {
        setIsLoading(false);
      }
    };

    loadFirmUsers();
  }, [user?.firm_id, hasAccess]);

  const handleAddUser = async () => {
    if (!selectedUserId || !currentEngagement) {
      setError('Please select a user');
      return;
    }

    if (!selectedRole) {
      setError('Please select a role');
      return;
    }

    // Check if user is already in the team
    const existingMember = teamMembers.find(member => member.id === selectedUserId);
    if (existingMember) {
      setError('User is already a member of this engagement');
      return;
    }

    try {
      setIsAdding(true);
      setError(null);
      setSuccess(null);

      await apiService.addUserToEngagement(currentEngagement.id, selectedUserId, selectedRole);
      
      // Reload team members
      const updatedTeam = await apiService.getEngagementTeam(currentEngagement.id);
      // Normalize team members structure
      const normalizedTeam = updatedTeam.map((member: any) => ({
        id: member.id,
        user_name: member.user_name || `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email,
        email: member.email,
        role: member.role || member.EngagementUser?.role || 'associate'
      }));
      setTeamMembers(normalizedTeam);
      
      setSuccess('User added successfully');
      setSelectedUserId("");
      setSelectedRole("associate"); // Reset to default
    } catch (error: any) {
      console.error('Error adding user:', error);
      setError(error.message || 'Failed to add user');
    } finally {
      setIsAdding(false);
    }
  };

  const handleDeleteClick = (member: TeamMember) => {
    const userName = member.user_name || member.email || 'User';
    setUserToDelete({ id: member.id, name: userName });
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete || !currentEngagement) {
      return;
    }

    try {
      setRemovingUserId(userToDelete.id);
      setError(null);
      setSuccess(null);

      await apiService.removeUserFromEngagement(currentEngagement.id, userToDelete.id);
      
      // Reload team members
      const updatedTeam = await apiService.getEngagementTeam(currentEngagement.id);
      // Normalize team members structure
      const normalizedTeam = updatedTeam.map((member: any) => ({
        id: member.id,
        user_name: member.user_name || `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email,
        email: member.email,
        role: member.role || member.EngagementUser?.role || 'associate'
      }));
      setTeamMembers(normalizedTeam);
      
      setSuccess('User removed from engagement successfully');
      setDeleteConfirmOpen(false);
      setUserToDelete(null);
    } catch (error: any) {
      console.error('Error removing user:', error);
      setError(error.message || 'Failed to remove user');
    } finally {
      setRemovingUserId(null);
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'engagement_partner':
        return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
      case 'engagement_manager':
        return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      case 'eqr_partner':
        return 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30';
      case 'eqr_manager':
        return 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30';
      case 'associate':
        return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'article':
        return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
      default:
        return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
    }
  };

  const formatRoleLabel = (role: string) => {
    return role
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (checkingAccess) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-[#4da3ff]" />
          <p className="text-white/60">Checking access...</p>
        </div>
      </div>
    );
  }

  if (!currentEngagement) {
    return (
      <div className="flex items-center justify-center h-full">
        <Card className="border-white/10 bg-white/5 max-w-md">
          <CardContent className="p-6">
            <div className="flex flex-col items-center gap-4 text-center">
              <Shield className="h-12 w-12 text-white/40" />
              <p className="text-sm text-white/60">Please select an engagement first.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Filter out users that are already in the team (only used when hasAccess)
  const availableUsers = firmUsers.filter(
    firmUser => !teamMembers.some(member => member.id === firmUser.id)
  );

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-white mb-2">Access and Roles</h2>
        <p className="text-sm text-white/60">
          {hasAccess
            ? "Manage user access to this engagement. Only users from your firm can be added."
            : "View engagement team and roles. Only Engagement Partners and Engagement Managers can add or remove users."}
        </p>
        {currentEngagement && (
          <p className="text-xs text-white/40 mt-1">
            Engagement: {currentEngagement.engagementName || currentEngagement.id.substring(0, 8)}
          </p>
        )}
      </div>

      {/* Add User Section - only enabled for engagement partner / manager */}
      <Card className={`border-white/10 ${!hasAccess ? "bg-white/5 opacity-80" : "bg-white/5"}`}>
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Add User to Engagement
            {!hasAccess && (
              <Badge variant="outline" className="text-xs text-white/50 border-white/20">
                Partner / Manager only
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-white/90">
              Select User
            </label>
            <Select value={selectedUserId} onValueChange={setSelectedUserId} disabled={!hasAccess}>
              <SelectTrigger className="disabled:opacity-60">
                <SelectValue placeholder={hasAccess ? "Choose a user to add..." : "Only Engagement Partner or Manager can add users"} />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-white/10">
                {isLoading ? (
                  <div className="p-4 text-center">
                    <Loader2 className="h-4 w-4 animate-spin mx-auto text-white/60" />
                  </div>
                ) : availableUsers.length === 0 ? (
                  <div className="p-4 text-center text-white/60 text-sm">
                    No available users to add
                  </div>
                ) : (
                  availableUsers.map((firmUser) => (
                    <SelectItem
                      key={firmUser.id}
                      value={firmUser.id}
                      className="text-white hover:bg-white/10 focus:bg-white/10"
                    >
                      <div className="flex flex-col">
                        <span>{firmUser.user_name}</span>
                        <span className="text-xs text-white/60">{firmUser.email}</span>
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-white/90">
              Assign Role
            </label>
            <Select value={selectedRole} onValueChange={setSelectedRole} disabled={!hasAccess}>
              <SelectTrigger className="disabled:opacity-60">
                <SelectValue placeholder="Select a role..." />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-white/10">
                {roleOptions.map((role) => (
                  <SelectItem
                    key={role.value}
                    value={role.value}
                    className="text-white hover:bg-white/10 focus:bg-white/10"
                  >
                    {role.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-white/50">
              This role will be assigned to the user for this engagement only.
            </p>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-md">
              <AlertCircle className="h-4 w-4 text-red-400" />
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {success && (
            <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/30 rounded-md">
              <CheckCircle className="h-4 w-4 text-green-400" />
              <p className="text-sm text-green-400">{success}</p>
            </div>
          )}

          <Button
            onClick={handleAddUser}
            disabled={!hasAccess || !selectedUserId || !selectedRole || isAdding || isLoading}
            className="w-full bg-[#4da3ff] hover:bg-[#4da3ff]/80 text-white disabled:opacity-60"
          >
            {isAdding ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Adding User...
              </>
            ) : (
              <>
                <UserPlus className="h-4 w-4 mr-2" />
                Add User with Role
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Current Team Members */}
      <Card className="border-white/10 bg-white/5">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Users className="h-5 w-5" />
            Current Team Members
          </CardTitle>
        </CardHeader>
        <CardContent>
          {teamMembers.length === 0 ? (
            <div className="text-center py-8 text-white/60">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-40" />
              <p>No team members yet</p>
            </div>
          ) : (
            <div className="rounded-md border border-white/10 overflow-hidden bg-black/20">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[32%] text-white/80">Name</TableHead>
                    <TableHead className="w-[38%] text-white/80">Email</TableHead>
                    <TableHead className="w-[20%] text-white/80">Role</TableHead>
                    <TableHead className="w-[10%] text-right text-white/80">
                      {hasAccess ? "Actions" : ""}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {teamMembers.map((member) => (
                    <TableRow key={member.id} className="hover:bg-white/5">
                      <TableCell className="align-middle">
                        <div className="flex flex-col">
                          <span className="font-medium text-white">
                            {member.user_name}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="align-middle">
                        <span className="text-sm text-white/70">
                          {member.email}
                        </span>
                      </TableCell>
                      <TableCell className="align-middle">
                        <Badge
                          variant="outline"
                          className={getRoleBadgeColor(member.role)}
                        >
                          {formatRoleLabel(member.role)}
                        </Badge>
                      </TableCell>
                      <TableCell className="align-middle text-right">
                        {hasAccess && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(member)}
                            disabled={removingUserId === member.id}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 disabled:opacity-50 disabled:pointer-events-none"
                            title="Remove from engagement"
                          >
                            {removingUserId === member.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-md bg-gray-900 border-white/20 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Remove User from Engagement</DialogTitle>
            <DialogDescription className="text-white/70">
              Are you sure you want to remove this user from the engagement?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-4 bg-black/40 rounded-lg border border-white/10">
              <p className="font-medium text-white">{userToDelete?.name}</p>
              <p className="text-sm text-white/60 mt-1">
                This will remove the user's access to this engagement only. The user will not be deleted from the system.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteConfirmOpen(false);
                setUserToDelete(null);
              }}
              disabled={removingUserId !== null}
              className="border-white/10 text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              disabled={removingUserId !== null}
              variant="destructive"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {removingUserId ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Removing...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Remove User
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

